/**
 * Group Assignment Modal
 * Ügyfelek hozzáadása csoporthoz modal
 */

import React, { useState } from 'react';
import { useCustomerGroups } from '../../hooks/useCustomerGroups';
import toast from 'react-hot-toast';
import { XMarkIcon, UserGroupIcon } from '@heroicons/react/24/outline';

interface GroupAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerIds: string[];
  onSuccess: () => void;
}

const GroupAssignmentModal: React.FC<GroupAssignmentModalProps> = ({
  isOpen,
  onClose,
  customerIds,
  onSuccess
}) => {
  const { groups, addCustomers } = useCustomerGroups();
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleAssign = async () => {
    if (!selectedGroupId) {
      toast.error('Válassz ki egy csoportot');
      return;
    }

    setLoading(true);
    try {
      await addCustomers(selectedGroupId, customerIds);
      toast.success(`${customerIds.length} ügyfél hozzáadva a csoporthoz`);
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Hiba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
        
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <UserGroupIcon className="h-6 w-6" />
              Hozzáadás csoporthoz
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {customerIds.length} ügyfél kerül hozzáadásra a kiválasztott csoporthoz
              </p>
            </div>

            {/* Csoport választó */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Válassz csoportot
              </label>
              {groups.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Nincs még csoport. Hozz létre egyet az Ügyfélcsoportok oldalon.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      onClick={() => setSelectedGroupId(group.id)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                        selectedGroupId === group.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-2xl flex-shrink-0"
                          style={{ backgroundColor: group.color || '#3B82F6' + '20' }}
                        >
                          {group.icon || '👥'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {group.name}
                          </div>
                          {group.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {group.description}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {group.customerCount} ügyfél
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Mégse
            </button>
            <button
              onClick={handleAssign}
              disabled={loading || !selectedGroupId || groups.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Hozzáadás...' : 'Hozzáadás csoporthoz'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupAssignmentModal;
